import { InsurancePlan } from 'src/services/interfaces/insurance';
import { isActivePrice } from '@utils/planFilters';

type Price = InsurancePlan['prices'][0];

export interface PriceSearchConditions {
  coverageAmount: number;
  primaryAge: number;
  spouseAge?: number;
  dependentsCount: number;
  hasSpouse: boolean;
  dependentBenefit?: number;
}

interface NewPriceData {
  mb: number;
  db: number;
  d: number;
  p: { min: number; max: number };
  s: { min: number; max: number } | null;
}

const isNewPriceFormat = (data: unknown): data is NewPriceData => {
  return !!data && typeof data === 'object' && 'mb' in data;
};

const findPriceNewFormat = (
  activePrices: Price[],
  conditions: PriceSearchConditions
): Price | undefined => {
  const {
    coverageAmount,
    primaryAge,
    spouseAge,
    dependentsCount,
    hasSpouse,
    dependentBenefit = 0,
  } = conditions;

  const hasDependents = dependentsCount > 0;

  // Determine coverage type based on spouse/dependents
  let coverageType: number;
  if (hasSpouse && hasDependents) {
    coverageType = 4; // Family
  } else if (hasSpouse) {
    coverageType = 2; // Primary+Spouse
  } else if (hasDependents) {
    coverageType = 3; // Primary+Deps
  } else {
    coverageType = 1; // Primary only
  }

  const newFormatPrices = activePrices.filter((price) =>
    isNewPriceFormat(price.data)
  );

  if (newFormatPrices.length === 0) return undefined;

  const matchesSpouseAge = (data: NewPriceData): boolean => {
    if (!hasSpouse || !data.s) return true;
    if (spouseAge === undefined) return true;
    return spouseAge >= data.s.min && spouseAge <= data.s.max;
  };

  // Find exact match
  const match = newFormatPrices.find((price) => {
    const data = price.data as NewPriceData;

    // Main benefit must match
    if (data.mb !== coverageAmount) return false;

    // Coverage type must match
    if (price.coverage !== coverageType) return false;

    // If dependents/family coverage, dependent benefit must match
    if (hasDependents) {
      if (data.d !== 1) return false;
      if (dependentBenefit > 0 && data.db !== dependentBenefit) return false;
    } else {
      if (data.d !== 0) return false;
    }

    // Primary age must be in range
    if (data.p.min >= primaryAge || data.p.max <= primaryAge) return false;

    // Spouse age must be in range
    if (!matchesSpouseAge(data)) return false;

    return true;
  });

  if (match) return match;

  // Fallback: find closest match by relaxing dependent benefit requirement
  const fallback = newFormatPrices
    .filter((price) => {
      const data = price.data as NewPriceData;
      if (data.mb !== coverageAmount) return false;
      if (price.coverage !== coverageType) return false;
      if (hasDependents && data.d !== 1) return false;
      if (!hasDependents && data.d !== 0) return false;
      if (data.p.min > primaryAge || data.p.max < primaryAge) return false;
      if (!matchesSpouseAge(data)) return false;
      return true;
    })
    .reduce<Price | undefined>((closest, current) => {
      if (!closest) return current;
      const closestData = closest.data as NewPriceData;
      const currentData = current.data as NewPriceData;
      const closestDiff = Math.abs(closestData.db - (dependentBenefit || 0));
      const currentDiff = Math.abs(currentData.db - (dependentBenefit || 0));
      return currentDiff < closestDiff ? current : closest;
    }, undefined);

  return fallback;
};

export const findValidCriticalIllnessPrice = (
  plan: InsurancePlan,
  conditions: PriceSearchConditions
): Price | undefined => {
  if (!plan.prices || plan.prices.length === 0) {
    return undefined;
  }

  const activePrices = plan.prices.filter(isActivePrice);

  // Try new format first
  const newFormatResult = findPriceNewFormat(activePrices, conditions);
  if (newFormatResult) return newFormatResult;

  // Fallback to old format
  const { coverageAmount, primaryAge, dependentsCount, hasSpouse } = conditions;
  let coverageType: number;
  if (hasSpouse && dependentsCount > 0) {
    coverageType = 4;
  } else if (hasSpouse) {
    coverageType = 2;
  } else if (dependentsCount > 0) {
    coverageType = 3;
  } else {
    coverageType = 1;
  }

  const oldFormatPrices = activePrices.filter(
    (price) => price.data && !isNewPriceFormat(price.data)
  );

  const exactMatch = oldFormatPrices.find((price) => {
    const priceData = price.data as {
      coverageAmount: number;
      dependentCount: number;
      minAge: number;
      maxAge: number;
    };
    return (
      priceData.coverageAmount === coverageAmount &&
      priceData.dependentCount === dependentsCount &&
      priceData.minAge <= primaryAge &&
      priceData.maxAge >= primaryAge &&
      price.coverage === coverageType
    );
  });

  if (exactMatch) return exactMatch;

  const closestMatch = oldFormatPrices
    .filter((price) => {
      const priceData = price.data as {
        coverageAmount: number;
        minAge: number;
        maxAge: number;
      };
      return (
        priceData.coverageAmount === coverageAmount &&
        priceData.minAge <= primaryAge &&
        priceData.maxAge >= primaryAge &&
        price.coverage === coverageType
      );
    })
    .reduce<Price | undefined>((closest, current) => {
      if (!closest) return current;
      const closestData = closest.data as { dependentCount: number };
      const currentData = current.data as { dependentCount: number };
      const closestDiff = Math.abs(
        closestData.dependentCount - dependentsCount
      );
      const currentDiff = Math.abs(
        currentData.dependentCount - dependentsCount
      );
      return currentDiff < closestDiff ? current : closest;
    }, undefined);

  return closestMatch;
};

export const getCriticalIllnessPriceId = (
  plan: InsurancePlan,
  conditions: PriceSearchConditions
): number | undefined => {
  const validPrice = findValidCriticalIllnessPrice(plan, conditions);
  return validPrice?.id;
};

export const findCriticalIllnessPriceId = (
  plan: InsurancePlan,
  coverageAmount: number,
  primaryAge: number,
  planTags: string[],
  spouseAge?: number,
  dependentBenefit?: number
): number | undefined => {
  const hasSpouse = planTags.some((tag) => tag.includes('spouse'));
  const dependentsCount = planTags.filter((tag) =>
    tag.includes('dependent')
  ).length;

  const conditions: PriceSearchConditions = {
    coverageAmount,
    primaryAge,
    spouseAge,
    dependentsCount,
    hasSpouse,
    dependentBenefit,
  };

  return getCriticalIllnessPriceId(plan, conditions);
};

export const calculateCriticalIllnessPrice = (
  coverageAmount: number,
  primaryAge: number,
  planTags: string[]
): number => {
  const getRateForAge = (age: number): number => {
    if (age < 25) return 0.48;
    if (age >= 25 && age <= 29) return 0.52;
    if (age >= 30 && age <= 34) return 0.69;
    if (age >= 35 && age <= 39) return 1.04;
    if (age >= 40 && age <= 44) return 1.56;
    if (age >= 45 && age <= 49) return 2.3;
    if (age >= 50 && age <= 54) return 3.29;
    if (age >= 55 && age <= 59) return 4.25;
    if (age >= 60 && age <= 64) return 5.16;
    if (age >= 65 && age <= 69) return 6.46;
    if (age >= 70 && age <= 74) return 8.62;
    return 11.44;
  };

  const insuranceUnits = coverageAmount / 1000;

  const adultRate = getRateForAge(primaryAge);
  const childRate = 0.09;

  const adultPrice = insuranceUnits * adultRate;
  const childPrice = insuranceUnits * childRate;

  const hasSpouse = planTags.some((tag) => tag.includes('spouse'));
  const childCount = planTags.filter((tag) => tag.includes('dependent')).length;

  let totalPrice = adultPrice;

  if (hasSpouse) {
    totalPrice += adultPrice;
  }

  if (childCount > 0) {
    totalPrice += childPrice * childCount;
  }

  return totalPrice;
};

/** Helper to get available dependent benefit options based on main benefit */
export const getAvailableDependentBenefits = (
  mainBenefit: number
): number[] => {
  const allOptions = [5000, 10000, 15000, 20000];
  const maxChildBenefit = mainBenefit / 2;
  return allOptions.filter((opt) => opt <= maxChildBenefit);
};
